import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Leaf, Clock, AlertTriangle, XCircle, Package, Search, Loader2, Timer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BUCKET_CONFIG = {
  fresh:       { label: 'Fresh Stock (0–30 days)',     icon: Leaf,           color: 'emerald', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
  mid_age:     { label: 'Mid Age (31–90 days)',        icon: Clock,          color: 'amber',   bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   badge: 'bg-amber-100 text-amber-800' },
  old_stock:   { label: 'Old Stock (90+ days)',        icon: Timer,          color: 'orange',  bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-700',  badge: 'bg-orange-100 text-orange-800' },
  near_expiry: { label: 'Near Expiry (< 30 days)',     icon: AlertTriangle,  color: 'red',     bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-700',     badge: 'bg-red-100 text-red-800' },
  expired:     { label: 'Expired Stock',               icon: XCircle,        color: 'rose',    bg: 'bg-rose-100',   border: 'border-rose-300',    text: 'text-rose-800',    badge: 'bg-rose-200 text-rose-900' },
};

export default function InventoryAgeing() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');

  useEffect(() => { fetchAgeing(); }, []);

  function fetchAgeing() {
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(API + '/inventory/ageing', { headers: { Authorization: 'Bearer ' + token } })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load ageing report'); setLoading(false); });
  }

  const { categories, brands } = useMemo(() => {
    if (!data) return { categories: [], brands: [] };
    const cats = new Set();
    const brs = new Set();
    Object.keys(BUCKET_CONFIG).forEach(key => {
      (data[key]?.items || []).forEach(item => {
        if (item.category) cats.add(item.category);
        if (item.brand) brs.add(item.brand);
      });
    });
    return { categories: Array.from(cats).sort(), brands: Array.from(brs).sort() };
  }, [data]);

  function filterItems(items) {
    return items.filter(item => {
      const term = searchTerm.toLowerCase();
      const matchSearch = !term || item.product_name.toLowerCase().includes(term) || (item.product_sku || '').toLowerCase().includes(term);
      const matchCat = filterCategory === 'all' || item.category === filterCategory;
      const matchBrand = filterBrand === 'all' || item.brand === filterBrand;
      return matchSearch && matchCat && matchBrand;
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading Ageing Report...</span>
      </div>
    );
  }

  if (!data) return <div className="text-center py-20 text-gray-500">No data available</div>;

  return (
    <div className="space-y-6" data-testid="inventory-ageing">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Inventory Ageing Report</h2>
          <p className="text-sm text-slate-500">Stock classification by age and expiry ({data.total_batches} batches)</p>
        </div>
        <Badge variant="outline" className="text-xs">{data.report_date}</Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => {
          const bucket = data[key] || { count: 0, total_qty: 0, total_value: 0 };
          const Icon = cfg.icon;
          return (
            <Card key={key} className={`${cfg.bg} ${cfg.border}`} data-testid={`ageing-card-${key}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${cfg.text}`} />
                  <span className={`text-xs font-semibold ${cfg.text} uppercase tracking-wide`}>{cfg.label}</span>
                </div>
                <p className={`text-2xl font-bold ${cfg.text}`}>{bucket.total_qty}</p>
                <p className="text-xs text-slate-500">₹{bucket.total_value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search product name or SKU..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="ageing-search" />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Brand" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bucket Tabs with Item Lists */}
      <Tabs defaultValue="near_expiry">
        <TabsList className="grid grid-cols-5 w-full">
          {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => (
            <TabsTrigger key={key} value={key} className="text-xs gap-1">
              {cfg.label.split('(')[0].trim()}
              <Badge className={`ml-1 text-xs ${cfg.badge}`}>{(data[key]?.items || []).length}</Badge>
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(BUCKET_CONFIG).map(([key, cfg]) => {
          const items = filterItems(data[key]?.items || []);
          return (
            <TabsContent key={key} value={key}>
              {items.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-10 h-10 mx-auto mb-2" />
                  <p>No items in this bucket{searchTerm || filterCategory !== 'all' || filterBrand !== 'all' ? ' (matching filters)' : ''}</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className={`${cfg.bg}`}>
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Product</th>
                        <th className="px-3 py-2 text-left font-semibold">Category</th>
                        <th className="px-3 py-2 text-left font-semibold">Brand</th>
                        <th className="px-3 py-2 text-center font-semibold">Batch</th>
                        <th className="px-3 py-2 text-center font-semibold">Qty</th>
                        <th className="px-3 py-2 text-right font-semibold">Value</th>
                        <th className="px-3 py-2 text-center font-semibold">MFG Date</th>
                        <th className="px-3 py-2 text-center font-semibold">Expiry Date</th>
                        <th className="px-3 py-2 text-center font-semibold">Age (Days)</th>
                        <th className="px-3 py-2 text-center font-semibold">Days to Expiry</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {items.map((item, idx) => (
                        <tr key={`${item.product_id}-${item.batch_number}-${idx}`} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-xs text-gray-400">{item.product_sku}</p>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{item.category || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{item.brand || '-'}</td>
                          <td className="px-3 py-2 text-center">
                            {item.batch_number ? <Badge variant="outline" className="text-xs">{item.batch_number}</Badge> : '-'}
                          </td>
                          <td className="px-3 py-2 text-center font-semibold">{item.quantity}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{item.total_value.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-center text-xs">{item.mfg_date || '-'}</td>
                          <td className="px-3 py-2 text-center text-xs">
                            {item.expiry_date ? (
                              <span className={item.days_to_expiry != null && item.days_to_expiry < 0 ? 'text-red-600 font-bold' : item.days_to_expiry != null && item.days_to_expiry <= 30 ? 'text-orange-600 font-semibold' : ''}>
                                {item.expiry_date}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {item.stock_age_days != null ? (
                              <Badge className={item.stock_age_days <= 30 ? 'bg-emerald-100 text-emerald-800' : item.stock_age_days <= 90 ? 'bg-amber-100 text-amber-800' : 'bg-orange-100 text-orange-800'}>
                                {item.stock_age_days}d
                              </Badge>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {item.days_to_expiry != null ? (
                              <Badge className={item.days_to_expiry < 0 ? 'bg-rose-200 text-rose-900' : item.days_to_expiry <= 30 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-700'}>
                                {item.days_to_expiry < 0 ? `Expired ${Math.abs(item.days_to_expiry)}d ago` : `${item.days_to_expiry}d`}
                              </Badge>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
